import { useCallback, useEffect, useRef } from 'react';
import { useJwt } from '../../../Hooks/useJwt';
import { useChatToken } from '../../../Hooks/useChatToken';
import { getChatToken, setChatToken } from '../../../ApiClientArchitecture/tokenStore';
import { ChatService } from '../services/chatService';
import { isTokenExpired, notifySessionExpired } from '../../../ApiClientArchitecture/session';

/** Refresh this long before the token expires. */
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;
/** Only guests who interacted this recently get a silent refresh; idle sessions are left to expire. */
const ACTIVE_WINDOW_MS = 30 * 60 * 1000;

/**
 * Sliding chat session: shortly before the token expires, an active guest gets a fresh one (the next request and
 * SignalR reconnect use it). An idle session expires; when the guest comes back to the tab it is sent to sign-in
 * straight away instead of meeting a 401 on the next message.
 */
export const useTokenRefresh = () => {
   const token = useChatToken();
   const { decodeJwt } = useJwt();
   const lastActivity = useRef(Date.now());
   const refreshing = useRef(false);

   const refresh = useCallback(async () => {
      if (refreshing.current) return;
      refreshing.current = true;
      try {
         const refreshed = await ChatService.refreshToken();
         if (refreshed && decodeJwt(refreshed)?.exp) setChatToken(refreshed);
         // A failed refresh with an already rejected token is reported by the HTTP client (401 → sign-in).
      } finally {
         refreshing.current = false;
      }
   }, [decodeJwt]);

   // Scheduled refresh for active guests.
   useEffect(() => {
      const exp = token ? decodeJwt(token)?.exp : undefined;
      if (!exp) return;
      const remaining = exp * 1000 - Date.now();
      if (remaining <= 0) return;
      // Never sooner than half the remaining lifetime, so short-lived tokens cannot cause a refresh loop.
      const delay = Math.max(remaining - REFRESH_BEFORE_EXPIRY_MS, remaining / 2);
      const timer = setTimeout(() => {
         if (Date.now() - lastActivity.current <= ACTIVE_WINDOW_MS) void refresh();
      }, delay);
      return () => clearTimeout(timer);
   }, [token, decodeJwt, refresh]);

   // Activity tracking; coming back to the tab checks the session at once.
   useEffect(() => {
      const markActive = () => {
         lastActivity.current = Date.now();
      };
      const onVisible = () => {
         if (document.visibilityState !== 'visible') return;
         markActive();
         const current = getChatToken();
         const exp = current ? decodeJwt(current)?.exp : undefined;
         if (!current) return;
         if (isTokenExpired(exp)) notifySessionExpired();
         else if (exp && exp * 1000 - Date.now() < REFRESH_BEFORE_EXPIRY_MS) void refresh();
      };
      window.addEventListener('keydown', markActive);
      window.addEventListener('pointerdown', markActive);
      document.addEventListener('visibilitychange', onVisible);
      return () => {
         window.removeEventListener('keydown', markActive);
         window.removeEventListener('pointerdown', markActive);
         document.removeEventListener('visibilitychange', onVisible);
      };
   }, [decodeJwt, refresh]);
};
